import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX, FiUpload, FiTrash2, FiImage } from "react-icons/fi";
import collegeService from "../../services/collegeService";

const DISCIPLINES = ["Pharmacy", "Nursing", "Law", "Physiotherapy", "Business Administration"];

const DEFAULT_SANSTHA = "MEHMUDA SHIKSHAN & MAHILA GRAMIN VIKAS BAHUDDESHIYA SANSTHA, NAGPUR";

const EMPTY_FORM = {
    name: "",
    sansthaName: DEFAULT_SANSTHA,
    code: "",
    discipline: "",
    address: "",
    city: "",
    state: "Maharashtra",
    pincode: "",
    contactEmail: "",
    contactPhone: "",
    establishedYear: "",
    isActive: true,
    logo: "", // string or { url, publicId }
};

function Field({ label, required, children, error, hint }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {hint && <span className="form-hint" style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>{hint}</span>}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function CollegeForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [form, setForm] = useState(EMPTY_FORM);
    const [logoPreview, setLogoPreview] = useState("");
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        if (!isEdit) return;
        collegeService
            .getById(id)
            .then((res) => {
                const c = res.data?.data ?? res.data;
                setForm({
                    name: c.name || "",
                    sansthaName: c.sansthaName || DEFAULT_SANSTHA,
                    code: c.code || "",
                    discipline: c.discipline || "",
                    address: c.address || "",
                    city: c.city || "",
                    state: c.state || "Maharashtra",
                    pincode: c.pincode || "",
                    contactEmail: c.contactEmail || "",
                    contactPhone: c.contactPhone || "",
                    establishedYear: c.establishedYear || "",
                    isActive: c.isActive !== undefined ? c.isActive : true,
                    logo: c.logo?.url || c.logo || "",
                });
                if (c.logo?.url || (typeof c.logo === "string" && c.logo)) {
                    setLogoPreview(c.logo?.url || c.logo);
                }
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || err.message);
                navigate("/colleges");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleLogoFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file (PNG, JPG, SVG, WebP).");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error("Logo file size must be under 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result;
            setLogoPreview(dataUrl);
            setForm((prev) => ({ ...prev, logo: dataUrl }));
            toast.success("Logo selected!");
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = () => {
        setLogoPreview("");
        setForm((prev) => ({ ...prev, logo: "" }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "College name is required.";
        if (!form.code.trim()) errs.code = "College code is required.";
        else if (form.code.trim().length < 2) errs.code = "Code must be at least 2 characters.";
        if (!form.discipline) errs.discipline = "Discipline is required.";
        if (form.pincode && !/^\d{6}$/.test(form.pincode)) errs.pincode = "Pincode must be 6 digits.";
        if (form.contactEmail && !/^\S+@\S+\.\S+$/.test(form.contactEmail)) errs.contactEmail = "Invalid email.";
        if (form.contactPhone && !/^\d{10}$/.test(form.contactPhone)) errs.contactPhone = "Must be 10 digits.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            const payload = { ...form };
            if (payload.establishedYear === "") delete payload.establishedYear;
            else payload.establishedYear = Number(payload.establishedYear);

            if (isEdit) {
                await collegeService.update(id, payload);
                toast.success("College updated successfully");
            } else {
                await collegeService.create(payload);
                toast.success("College created successfully");
            }
            navigate("/colleges");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading…</div>;

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit College" : "Add College"}</h1>
                    <p className="page-subtitle">Configure college profile, sanstha details, and official logo for report printing</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/colleges")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                        <FiSave size={14} /> {saving ? "Saving…" : "Save College"}
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card" style={{ marginBottom: "1.5rem" }}>
                    <div className="card-header" style={{ fontWeight: 700 }}>Institutional Identity &amp; Logo</div>
                    <div className="card-body">
                        {/* Logo Upload Section */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.5rem",
                            padding: "1.25rem",
                            background: "var(--bg, #f8fafc)",
                            border: "1px solid var(--border, #e2e8f0)",
                            borderRadius: "10px",
                            marginBottom: "1.5rem",
                            flexWrap: "wrap"
                        }}>
                            <div style={{
                                width: 90,
                                height: 90,
                                borderRadius: "10px",
                                background: "#fff",
                                border: "2px dashed var(--border, #cbd5e1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                                flexShrink: 0,
                                position: "relative"
                            }}>
                                {logoPreview ? (
                                    <img
                                        src={logoPreview}
                                        alt="College Logo"
                                        style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }}
                                    />
                                ) : (
                                    <FiImage size={32} style={{ color: "var(--text-muted, #94a3b8)" }} />
                                )}
                            </div>

                            <div style={{ flex: 1, minWidth: "220px" }}>
                                <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "4px" }}>Official College Logo</div>
                                <p style={{ fontSize: "12px", color: "var(--text-muted, #64748b)", margin: "0 0 10px" }}>
                                    This logo appears on all printed fee receipts, student statements, salary slips, and official certificates.
                                    Recommended format: PNG, JPG, or SVG (transparent background preferred).
                                </p>
                                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleLogoFile}
                                        accept="image/*"
                                        style={{ display: "none" }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <FiUpload size={13} /> {logoPreview ? "Change Logo" : "Upload Logo"}
                                    </button>
                                    {logoPreview && (
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            onClick={handleRemoveLogo}
                                            style={{ color: "var(--danger, #dc2626)" }}
                                        >
                                            <FiTrash2 size={13} /> Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <Field
                                    label="Sanstha / Society / Trust Name"
                                    hint="Printed as the header on all official receipts, statements, and reports."
                                >
                                    <input
                                        name="sansthaName"
                                        value={form.sansthaName}
                                        onChange={handleChange}
                                        placeholder="e.g. MEHMUDA SHIKSHAN & MAHILA GRAMIN VIKAS BAHUDDESHIYA SANSTHA, NAGPUR"
                                        className="form-control"
                                    />
                                </Field>
                            </div>

                            <Field label="College Name" required error={errors.name}>
                                <input
                                    name="name"
                                    value={form.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Central India College of Pharmacy"
                                    className={`form-control ${errors.name ? "error" : ""}`}
                                    autoFocus
                                />
                            </Field>

                            <Field label="Code" required error={errors.code} hint="Used in receipt and document prefixes (e.g. CICP)">
                                <input
                                    name="code"
                                    value={form.code}
                                    onChange={handleChange}
                                    placeholder="e.g. CICP"
                                    className={`form-control ${errors.code ? "error" : ""}`}
                                    style={{ textTransform: "uppercase" }}
                                    maxLength={10}
                                />
                            </Field>

                            <Field label="Discipline" required error={errors.discipline}>
                                <select
                                    name="discipline"
                                    value={form.discipline}
                                    onChange={handleChange}
                                    className={`form-control ${errors.discipline ? "error" : ""}`}
                                >
                                    <option value="">Select discipline…</option>
                                    {DISCIPLINES.map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Established Year">
                                <input
                                    type="number"
                                    name="establishedYear"
                                    value={form.establishedYear}
                                    onChange={handleChange}
                                    placeholder="e.g. 2005"
                                    className="form-control"
                                />
                            </Field>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ marginBottom: "1.5rem" }}>
                    <div className="card-header" style={{ fontWeight: 700 }}>Contact &amp; Location Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Campus Address</label>
                                <input
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    placeholder="e.g. Kh. No. 25/1 B, Mouza-Lonara"
                                    className="form-control"
                                />
                            </div>

                            <Field label="City">
                                <input name="city" value={form.city} onChange={handleChange} placeholder="e.g. Nagpur" className="form-control" />
                            </Field>
                            <Field label="State">
                                <input name="state" value={form.state} onChange={handleChange} placeholder="e.g. Maharashtra" className="form-control" />
                            </Field>
                            <Field label="Pincode" error={errors.pincode}>
                                <input name="pincode" value={form.pincode} onChange={handleChange} placeholder="e.g. 441111" className={`form-control ${errors.pincode ? "error" : ""}`} />
                            </Field>

                            <Field label="Contact Email" error={errors.contactEmail}>
                                <input name="contactEmail" value={form.contactEmail} onChange={handleChange} placeholder="e.g. info@cicop.edu.in" className={`form-control ${errors.contactEmail ? "error" : ""}`} />
                            </Field>
                            <Field label="Contact Phone" error={errors.contactPhone}>
                                <input name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="e.g. 07122537444" className={`form-control ${errors.contactPhone ? "error" : ""}`} />
                            </Field>

                            {isEdit && (
                                <Field label="Status">
                                    <select
                                        name="isActive"
                                        value={form.isActive ? "active" : "inactive"}
                                        onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "active" }))}
                                        className="form-control"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </Field>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}